import _ from 'lodash';
import Actions from '../actions/actions';
import ButtonActions from '../actions/ButtonCRUDActions';
import PageActions from '../actions/PageCRUDActions';
import { entryCRUDActions } from '../actions/EntryCRUDActions';
import WidgetActions from '../actions/WidgetCRUDActions';
import WidgetModelHelpers from '../helpers/widget-model-helpers';
import { WidgetState } from '../interfaces/widget';
import {
  deleteLocalFirst,
  setBusy,
  setUnBusy,
  update,
} from './common/commonReducers';

const initialState: WidgetState = {
  busy: false,
  entities: [],
  busyWidgets: [],
};

type RetrieveAllByPageIdAction = ReturnType<
  typeof WidgetActions.retrieveAllByPageIdActionCreator
>;

const addWidget = (state: WidgetState, action) => {
  const newState = _.cloneDeep(state);
  const entities = _.sortBy([...newState.entities, action.entity], 'sortOrder');

  return { ...state, entities, busy: false };
};

const retrievedAllByPageId = (
  state: WidgetState,
  action: RetrieveAllByPageIdAction,
): WidgetState => {
  const { pageId, widgets } = action;

  // Remove all widgets with the current pageId
  const filteredEntities = state.entities.filter(
    widget => widget.pageId !== pageId,
  );

  const entities = [...filteredEntities, ...widgets];

  return {
    ...state,
    busy: false,
    entities,
  };
};

const unsetEntities = (state: WidgetState): WidgetState => {
  return {
    ...state,
    entities: [],
  };
};

// tslint:disable-next-line:cyclomatic-complexity
export default function widget(state = initialState, action): WidgetState {
  switch (action.type) {
    case WidgetActions.busyAction:
      return setBusy(state);
    case WidgetActions.retrieveAllByPageIdAction:
      return retrievedAllByPageId(state, action);
    case WidgetActions.updateAction: {
      const { entity } = action;
      return update(state, { entity });
    }
    case WidgetActions.updateLocalAction: {
      const { entity } = action;
      return update(state, { entity });
    }
    case WidgetActions.unsetEntitiesAction:
      return unsetEntities(state);
    case WidgetActions.deleteLocalAction:
      return deleteLocalFirst(state, action);
    case WidgetActions.retrievedAction:
      return addWidget(state, action);
    case entryCRUDActions.busyAction:
      return setBusy(state);
    case entryCRUDActions.retrievedAction: {
      const { entry, widgetId } = action.payload;
      const newState = _.cloneDeep(state);
      newState.busy = false;

      const widgetIndex = newState.entities.findIndex(e => e.id === widgetId);

      const widgetInstance = newState.entities[widgetIndex];

      if (
        widgetInstance &&
        widgetInstance.config &&
        widgetInstance.config.entries
      ) {
        widgetInstance.config.entries.push(entry);
      }

      return newState;
    }

    case entryCRUDActions.updateAction: {
      const newState = _.cloneDeep(state);
      newState.busy = false;
      const { widgetId, ...entity } = action.entity;
      const widgetInstance = newState.entities.find(e => e.id === widgetId);

      if (widgetInstance) {
        WidgetModelHelpers.updateEntry(widgetInstance, entity);
      }

      return newState;
    }

    case entryCRUDActions.deleteLocalAction: {
      const newState = _.cloneDeep(state);
      newState.busy = false;
      const { widgetId } = action.entity;
      const widgetInstance = newState.entities.find(e => e.id === widgetId);

      if (widgetInstance) {
        WidgetModelHelpers.deleteEntry(widgetInstance, action.entity.id);
      }

      return newState;
    }

    case ButtonActions.busyAction:
      return setBusy(state);
    case ButtonActions.retrievedAction: {
      const newState = _.cloneDeep(state);
      const { widgetId, button } = action.payload;
      newState.busy = false;

      const widgetInstance = newState.entities.find(e => e.id === widgetId);

      if (widgetInstance) {
        widgetInstance.buttons.push(button);
      }

      return newState;
    }

    case WidgetActions.setBusyWidgetAction: {
      const { busyWidgets } = _.cloneDeep(state);
      if (busyWidgets.indexOf(action.widgetId)) {
        busyWidgets.push(action.widgetId);
      }
      return {
        ...state,
        busyWidgets,
      };
    }

    case WidgetActions.setUnBusyWidgetAction: {
      const { busyWidgets } = _.cloneDeep(state);
      const index = busyWidgets.indexOf(action.widgetId);
      if (index > -1) {
        busyWidgets.splice(index, 1);
      }
      return {
        ...state,
        busyWidgets,
      };
    }

    case WidgetActions.batchDeleteLocalAction: {
      const { widgetIds: widgetIdsToDelete } = action;
      return {
        ...state,
        entities: state.entities.filter(
          widget => !widgetIdsToDelete.includes(widget.id),
        ),
      };
    }

    case ButtonActions.updateAction: {
      const newState = _.cloneDeep(state);
      newState.busy = false;
      const { widgetId } = action.entity;
      const entityCopy = _.cloneDeep(action.entity);
      delete entityCopy.widgetId;

      const widgetInstance = newState.entities.find(e => e.id === widgetId);

      if (widgetInstance) {
        WidgetModelHelpers.updateButton(widgetInstance, entityCopy);
      }

      return newState;
    }
    case ButtonActions.deleteLocalAction: {
      const newState = _.cloneDeep(state);
      newState.busy = false;
      const { widgetId } = action.entity;

      const widgetInstance = newState.entities.find(e => e.id === widgetId);

      if (widgetInstance) {
        WidgetModelHelpers.deleteButton(widgetInstance, action.entity.id);
      }

      return newState;
    }

    case ButtonActions.retrievedEntryAction: {
      const newState = _.cloneDeep(state);
      newState.busy = false;
      const { widgetId, entryId, button } = action.payload;

      const widgetInstance = newState.entities.find(e => e.id === widgetId);

      if (
        widgetInstance &&
        widgetInstance.config &&
        widgetInstance.config.entries
      ) {
        const entryInstance = widgetInstance.config.entries.find(
          e => e.id === entryId,
        );

        if (entryInstance) {
          entryInstance.buttons.push(button);
        }
      }

      return newState;
    }

    case ButtonActions.updateEntryAction: {
      const newState = _.cloneDeep(state);

      const { widgetId, entryId } = action.entity;
      const entityCopy = _.cloneDeep(action.entity);
      delete entityCopy.widgetId;
      delete entityCopy.entryId;
      const widgetInstance = newState.entities.find(e => e.id === widgetId);

      if (widgetInstance) {
        WidgetModelHelpers.updateEntryButton(
          widgetInstance,
          entryId,
          entityCopy,
        );
      }

      return newState;
    }
    case ButtonActions.deleteEntryAction: {
      const newState = _.cloneDeep(state);
      newState.busy = false;
      const { widgetId, entryId } = action.entity;
      const widgetInstance = newState.entities.find(e => e.id === widgetId);

      if (widgetInstance) {
        WidgetModelHelpers.deleteEntryButton(
          widgetInstance,
          entryId,
          action.entity.id,
        );
      }

      return newState;
    }
    case Actions.UNBUSY:
      return setUnBusy(state);
    case Actions.RESET:
      return initialState;

    case PageActions.retrievedByGroupId: {
      if (Array.isArray(action.payload)) {
        return action.payload.reduce((s, page) => {
          return retrievedAllByPageId(
            s,
            WidgetActions.retrieveAllByPageIdActionCreator(
              page.id || -1,
              page.widgets || [],
            ),
          );
        }, state);
      }
      return state;
    }

    default:
      return state;
  }
}
